import React from 'react';
import { t } from '@lingui/macro';

import { MicrodataHTMLAttributes } from '@rover/components/src/microdata';
import type { Icon } from '@rover/icons';
import ReviewStarHalfOutline from '@rover/icons/svg/review-half-star-outline.svg';
import ReviewStar from '@rover/icons/svg/review-star.svg';
import ReviewStarFill from '@rover/icons/svg/review-star-fill.svg';
import ReviewStarHalf from '@rover/icons/svg/review-star-half.svg';
import type {
  ThemeFontWeight,
  ThemeTextColor,
  ThemeTextSizes,
} from '@rover/kibble/styles/themeTypes';
import useTheme from '@rover/kibble/themes/useTheme';

export type StarRatingCommonProps = {
  rating: number;
  size?: number;
  textSize?: ThemeTextSizes;
  fontWeight?: ThemeFontWeight;
  variant?: StarRatingVariant;
  maxRating?: number;
  minRating?: number;
  disabled?: boolean;
  textColor?: ThemeTextColor;
  emptyValue?: string;
} & MicrodataHTMLAttributes;

export type StarRatingComponentType<Props extends StarRatingCommonProps> = (
  props: Props
) => JSX.Element;

export const STAR_SIZE = 16;

export const MIN_RATING = 1;
export const MAX_RATING = 5;

export type StarRatingVariant = 'expand' | 'outline' | 'compact';

type StarProps = {
  size: number;
  disabled: boolean;
};

export const getAccessibilityLabel = (
  starValue: number | null,
  maxValue: number = MAX_RATING
): string => {
  if (starValue === null) return t`No star rating available`;
  const starRating = starValue.toFixed(1);
  const maxRating = maxValue.toFixed(0);
  return t`${starRating} out of ${maxRating} stars`;
};

export const getStarRating = (rating: number, maxRating: number): number => {
  let stars = rating;
  if (stars < 0) {
    stars = 0;
  } else if (stars > maxRating) {
    stars = maxRating;
  }
  return stars;
};

export const useStarColor = (disabled: boolean = false): string => {
  const { textColor, colors } = useTheme();
  return disabled ? textColor.disabled : colors.yellow['400'];
};

export const FullStar = ({ size, disabled }: StarProps): Icon => {
  const fillColor = useStarColor(disabled);
  return <ReviewStarFill height={size} width={size} fill={fillColor} />;
};

export const HalfStar = ({ size, disabled }: StarProps): Icon => {
  const fillColor = useStarColor(disabled);
  return <ReviewStarHalf height={size} width={size} fill={fillColor} />;
};

export const FullStarOutline = ({ size, disabled }: StarProps): Icon => {
  const fillColor = useStarColor(disabled);
  return <ReviewStar height={size} width={size} fill={fillColor} />;
};

export const HalfStarOutline = ({ size, disabled }: StarProps): Icon => {
  const fillColor = useStarColor(disabled);
  return <ReviewStarHalfOutline height={size} width={size} fill={fillColor} />;
};

export const getStars = (
  numWholeStars: number,
  numEmptyStars: number,
  hasHalfStar: boolean,
  outline: boolean,
  size: number,
  disabled: boolean
): Icon[] => {
  const stars: JSX.Element[] = [];

  // Add full stars
  for (let i = 0; i < numWholeStars; i += 1) {
    stars.push(<FullStar size={size} disabled={disabled} key={`full-star-${i}`} />);
  }

  // Add half stars
  if (hasHalfStar) {
    if (outline) {
      stars.push(<HalfStarOutline size={size} disabled={disabled} key="half-star-outline" />);
    } else {
      stars.push(<HalfStar size={size} disabled={disabled} key="half-star" />);
    }
  }

  // Add empty stars
  if (outline) {
    for (let i = 0; i < numEmptyStars; i += 1) {
      stars.push(
        <FullStarOutline size={size} disabled={disabled} key={`full-star-outline-${i}`} />
      );
    }
  }

  return stars;
};

export const generateStars = (
  variant: StarRatingVariant,
  rating: number | null,
  maxRating: number,
  disabled: boolean
): Icon[] => {
  const starSize = STAR_SIZE;

  if (variant === 'compact') {
    return [<FullStar size={starSize} key="compact-star-0" disabled={disabled} />];
  }

  if (rating === null) return [];

  const hasHalfStar = rating % 1 !== 0;
  const numWholeStars = Math.floor(rating);
  const numEmptyStars = maxRating - Math.ceil(rating);

  if (variant === 'expand') {
    return getStars(numWholeStars, numEmptyStars, hasHalfStar, false, starSize, disabled);
  }

  if (variant === 'outline') {
    return getStars(numWholeStars, numEmptyStars, hasHalfStar, true, starSize, disabled);
  }

  return [];
};
